import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CollectionActionRecord, CreateCollectionCasePayload, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-collection-case-detail',
  imports: [
    DatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TableModule, CustomerSelectorComponent, CurrencySelectorComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.collections.form.createTitle') : t('sales.collections.form.editTitle')" [description]="t('sales.collections.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card"><a class="back-link" [routerLink]="['/sales/collection-cases']">{{ t('catalog.action.backToList') }}</a></header>
        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.collections.fields.caseNumber') }}</span><input pInputText [(ngModel)]="form.caseNumber" /></label>
            <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
            <label><span>{{ t('sales.collections.fields.overdue') }}</span><p-inputNumber [(ngModel)]="form.totalOverdueAmount" [min]="0" /></label>
            <label><span>{{ t('sales.installments.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </section>
        } @else {
          <section class="card">
            <p>{{ t('sales.collections.fields.caseNumber') }}: <strong>{{ caseNumber() }}</strong> — {{ status() }}</p>
          </section>
          <section class="card grid">
            <label>
              <span>{{ t('sales.collections.fields.actionType') }}</span>
              <p-select [options]="actionTypes" optionValue="value" [(ngModel)]="actionForm.actionType" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label class="full"><span>{{ t('sales.collections.fields.actionNotes') }}</span><input pInputText [(ngModel)]="actionForm.notes" /></label>
            <button pButton type="button" [label]="t('sales.collections.addAction')" (click)="addAction()"></button>
            <button pButton type="button" severity="secondary" [label]="t('sales.collections.resolve')" (click)="resolve()"></button>
          </section>
          <p-table [value]="actions()">
            <ng-template pTemplate="header"><tr><th>{{ t('sales.collections.fields.actionType') }}</th><th>{{ t('sales.collections.fields.actionNotes') }}</th><th>{{ t('sales.collections.fields.performedAt') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ row.actionType }}</td><td>{{ row.notes }}</td><td>{{ row.performedAtUtc | date:'short' }}</td></tr></ng-template>
          </p-table>
        }
      }
    </ain-page>
  `,
  styles: [`.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px}.back-link{color:#1d4ed8;font-weight:600;text-decoration:none}.grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}label{display:grid;gap:6px}label.full{grid-column:1/-1}`]
})
export class SalesCollectionCaseDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreateMode = signal(false);
  readonly caseNumber = signal('');
  readonly status = signal('');
  readonly actions = signal<CollectionActionRecord[]>([]);
  form = { caseNumber: '', customerAccountId: '' as string | null, totalOverdueAmount: 0, currencyCode: 'IRR' };
  actionForm = { actionType: 'Call', notes: '' };
  actionTypes = [
    { value: 'Call', labelKey: 'sales.collections.actions.call' },
    { value: 'Email', labelKey: 'sales.collections.actions.email' },
    { value: 'Note', labelKey: 'sales.collections.actions.note' },
    { value: 'PromiseToPay', labelKey: 'sales.collections.actions.promise' }
  ];
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('caseId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) { this.loading.set(false); return; }
    this.load(id!);
  }
  load(id: string): void {
    this.api.getCollectionCase(id).subscribe({
      next: c => { this.caseNumber.set(c.caseNumber); this.status.set(c.status); this.loading.set(false); }
    });
    this.api.listCollectionActions(id).subscribe({ next: a => this.actions.set(a) });
  }
  save(): void {
    if (!this.form.customerAccountId) return;
    const payload: CreateCollectionCasePayload = { ...this.form, customerAccountId: this.form.customerAccountId, riskScore: null, openedAtUtc: null, notes: null };
    this.api.createCollectionCase(payload).subscribe({
      next: c => void this.router.navigate(['/sales/collection-cases', c.id])
    });
  }
  addAction(): void {
    const id = this.route.snapshot.paramMap.get('caseId')!;
    this.api.addCollectionAction(id, { actionType: this.actionForm.actionType, notes: this.actionForm.notes }).subscribe({
      next: () => this.load(id)
    });
  }
  resolve(): void {
    const id = this.route.snapshot.paramMap.get('caseId')!;
    this.api.updateCollectionCase(id, { status: 'Resolved', notes: null }).subscribe({
      next: () => this.load(id)
    });
  }
  t(key: string): string { return this.localization.translate(key); }
}
